import React, { lazy, Suspense, useRef, useState } from 'react';
import { bibleIcon, type IPassageLink } from './PassageLink.js';
import { Button, Modal } from 'react-bootstrap';
import { convertRefToUsfm, getFormattedRef } from '@soulhx/fs-common';
import { useUserSetting } from '@soulhx/client-store';
import { clsx, useMousePreview } from '@soulhx/ui-common';
import { PassagePreviewPopover } from './PassagePreviewPopover.js';
const BibleViewer = lazy(() => import('../viewer/BibleViewer.js'));

type IPassageLinkModal = Pick<
  IPassageLink,
  'children' | 'passage' | 'context' | 'includeVerses' | 'version' | 'className' | 'options' | 'noPreview'
>;

export const PassageLinkModal = ({
  children,
  passage,
  context,
  includeVerses,
  version,
  className,
  options = undefined,
  noPreview = false,
}: IPassageLinkModal) => {
  const [showModal, setShowModal] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { show: showPreview, targetHandlers, overlayHandlers } = useMousePreview();
  const { settingValue: bibleViewSettingString, update: updateBibleViewSettingString } =
    useUserSetting<string>('readBibleViewSettings');

  const optionsToUse = options || bibleViewSettingString;

  return (
    <>
      <Button
        ref={buttonRef}
        variant="link"
        onClick={() => setShowModal((prev) => !prev)}
        className={clsx('p-0', 'align-baseline', className)}
        {...(noPreview ? {} : targetHandlers)}
      >
        {children ?? getFormattedRef(passage, { includeVerses, context })}
        {bibleIcon}
      </Button>

      {!noPreview && (
        <PassagePreviewPopover
          target={buttonRef}
          show={showPreview && !showModal}
          overlayHandlers={overlayHandlers}
          passage={passage}
          version={version}
          context={context}
          includeVerses={includeVerses}
        />
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>{getFormattedRef(passage, { includeVerses, context })}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Suspense fallback=". . .">
            <BibleViewer
              key={`fs-viewer-${optionsToUse}`}
              passage={convertRefToUsfm(passage, { context })}
              version={version}
              fullCopyright
              showFullPassageControl
              options={optionsToUse}
              updateDefaultOptionsCallback={options && options.length > 0 ? undefined : updateBibleViewSettingString}
            />
          </Suspense>
        </Modal.Body>
      </Modal>
    </>
  );
};
